import styled, { css, DefaultTheme } from 'styled-components'

import {
  dropdownListScrollbar,
  dropdownListStyles,
  getDropdownListItemStyles,
} from '../dropdown-list'
import { ChipSelectSize } from './chip-select.types'

const inputWrapperMinHeightMap: Record<ChipSelectSize, string> = {
  large: '4.8rem',
  medium: '3.8rem',
  small: '3.2rem',
}

const getBorderColor = (theme: DefaultTheme, hasError: boolean, isOpen: boolean) => {
  if (hasError) return theme.colors.red300
  if (isOpen) return theme.colors.primary800
  return theme.colors.gray500
}

const getHoverBorderColor = (theme: DefaultTheme, hasError: boolean, disabled: boolean) => {
  if (hasError) return theme.colors.red300
  if (disabled) return theme.colors.gray500
  return theme.colors.gray800
}

export const Container = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  width: '100%',
}))

export const InputWrapper = styled.div<{
  $hasError: boolean
  $isOpen: boolean
  $disabled: boolean
  $readOnly: boolean
  $size: ChipSelectSize
}>(({ theme, $hasError, $isOpen, $disabled, $size }) => ({
  '&:hover': {
    borderColor: getHoverBorderColor(theme, $hasError, $disabled),
  },
  alignItems: 'center',
  background: $disabled ? theme.colors.gray300 : theme.colors.white,
  border: `${theme.border.size['1']} solid ${getBorderColor(theme, $hasError, $isOpen)}`,
  borderRadius: theme.border.radius['4'],
  cursor: $disabled ? 'not-allowed' : 'text',
  display: 'flex',
  gap: theme.space['4'],
  minHeight: inputWrapperMinHeightMap[$size],
  opacity: $disabled ? 0.6 : 1,
  padding: `calc(${theme.space['6']} - ${theme.border.size['1']}) calc(${theme.space['10']} - ${theme.border.size['1']})`,
  position: 'relative',
  transition: `border-color ${theme.transition.duration.sm} ease`,
}))

export const ChipsAndInput = styled.div<{ $showBadge: boolean; $showCaret: boolean }>(
  ({ theme, $showBadge, $showCaret }) => {
    // The badge/caret adornment is absolutely positioned and vertically centered
    // against the box's full height, so it no longer takes up flex space on its
    // own — reserve room here instead, so wrapped chips never sit under it.
    const badgeWidth = $showBadge ? 3.2 : 0
    const caretWidth = $showCaret ? 2 : 0
    const innerGap = $showBadge && $showCaret ? 0.4 : 0
    const edgeGap = $showBadge || $showCaret ? 0.4 : 0

    return {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      flexWrap: 'wrap',
      gap: theme.space['4'],
      minWidth: 0,
      paddingRight: `${badgeWidth + caretWidth + innerGap + edgeGap}rem`,
    }
  },
)

export const InlineInput = styled.input<{ $size: ChipSelectSize }>(({ theme, $size }) => {
  const fontSizeMap: Record<ChipSelectSize, string> = {
    large: '1.6rem',
    medium: '1.4rem',
    small: '1.2rem',
  }

  return {
    '&::placeholder': {
      color: theme.colors.primary500,
    },
    background: 'transparent',
    border: 'none',
    color: theme.colors.black600,
    flex: '1 1 6rem',
    fontSize: fontSizeMap[$size],
    lineHeight: '2rem',
    minWidth: '6rem',
    outline: 'none',
    padding: 0,
  }
})

// Note: separates the selected-tag chips from the input, only shown once
// there's at least one chip to separate from.
export const TagDivider = styled.div(({ theme }) => ({
  alignSelf: 'stretch',
  background: theme.colors.gray400,
  flexShrink: 0,
  width: theme.border.size['1'],
}))

export const RightAdornment = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['4'],
  // Absolutely positioned + centered via transform instead of flex `alignItems`,
  // since the sibling chips can wrap across multiple lines: flex cross-axis
  // centering follows the tallest line's box, which drifted this off from the
  // chips the user actually sees. This centers against the box's real height.
  position: 'absolute',
  right: `calc(${theme.space['10']} - ${theme.border.size['1']})`,
  top: '50%',
  transform: 'translateY(-50%)',
}))

export const CaretIcon = styled.div<{ $isOpen: boolean }>(({ theme, $isOpen }) => ({
  alignItems: 'center',
  borderRadius: theme.border.radius['4'],
  color: theme.colors.black600,
  display: 'flex',
  flexShrink: 0,
  padding: theme.space['4'],
  transform: $isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: `transform ${theme.transition.duration.sm} ease`,
}))

// Note: the shared design-system dropdown panel and rows - per the tag-select design the rows
// are always the small (32px) "Dropdown List Item", whatever the field's own size.
export const Dropdown = styled.div(
  ({ theme }) => css`
    ${dropdownListStyles};

    display: flex;
    flex-direction: column;
    max-height: 28rem;
    overflow: hidden;
    z-index: ${theme.zIndex.level3};
  `,
)

export const OptionsList = styled.div(
  () => css`
    ${dropdownListScrollbar};

    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-gutter: stable;
  `,
)

export const CheckboxWrapper = styled.div(() => ({
  alignItems: 'center',
  display: 'flex',
  pointerEvents: 'none',
}))

export const OptionRow = styled.div<{ $isSelected: boolean }>(({ theme }) =>
  getDropdownListItemStyles(theme, { size: 'small' }),
)

export const OptionLabel = styled.span({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const NoMatch = styled.div(({ theme }) => ({
  color: theme.colors.black300,
  fontSize: '1.4rem',
  padding: `${theme.space['12']}`,
  textAlign: 'center',
}))

export const CreateOption = styled.div(({ theme }) => ({
  ...getDropdownListItemStyles(theme, { size: 'small' }),
  borderTop: `${theme.border.size['1']} solid ${theme.colors.gray200}`,
}))
